import { useSelector } from 'react-redux'

import { toast } from '@webapp/components/Notification'
import { useEntitlement } from '@webapp/core/hooks/useEntitlement'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { FinancialAccountTypes } from '../../entities/enums/FinancialAccountTypeEnum'
import { useGetFinancialAccountPresenter } from '../../features/get-financial-account/presenters/GetFinancialAccountPresenter'
import { useGetFinancialAccountBalanceSummaryPresenter } from '../../features/get-financial-account-balance-summary/presenters/GetFinancialAccountBalanceSummaryPresenter'
import { useLongTermScaVerificationPresenter } from '../../features/verify-identity/presenters/LongTermScaVerificationPresenter'

export const useFinancialAccountSummaryCard = () => {
  const intl = useProcurifyIntl()

  const entitlements = useEntitlement()
  const { bill_pay: isEntitledToBillPayments } = useSelector(
    makeSelectFeatureSwitches()
  )
  const isEntitledToSpendingCards = entitlements.module.spendingCard.enabled

  const {
    data: { financialAccount },
    states: { isFetching: isFetchingFinancialAccount },
  } = useGetFinancialAccountPresenter()

  const {
    data: { scaToken },
    commands: { verifyIdentity },
    states: { isLoading: isLoadingScaVerification },
  } = useLongTermScaVerificationPresenter({
    enabled:
      financialAccount?.type === FinancialAccountTypes.AIRWALLEX_GBP &&
      financialAccount?.isOperational,
    onError: () => {
      toast.error({
        title: intl.formatMessage(messages.verifyIdentityErrorToastMessage),
      })
    },
  })

  const showBalanceSummary =
    financialAccount?.isOperational &&
    (financialAccount?.type === FinancialAccountTypes.AIRWALLEX_GBP
      ? !!scaToken
      : true)

  const {
    data: { balanceSummary },
    states: { isFetching: isFetchingBalanceSummary },
  } = useGetFinancialAccountBalanceSummaryPresenter({
    financialAccountId: financialAccount?.id,
    enabled: showBalanceSummary,
    scaToken,
  })

  const getAvailableFundsTooltipText = () => {
    if (isEntitledToBillPayments && isEntitledToSpendingCards) {
      return intl.formatMessage(
        messages.availableFundsSpendingCardsPaymentsTooltipText
      )
    }
    if (isEntitledToBillPayments) {
      return intl.formatMessage(messages.availableFundsPaymentsTooltipText)
    }
    if (isEntitledToSpendingCards) {
      return intl.formatMessage(messages.availableFundsSpendingCardsTooltipText)
    }
    return undefined
  }

  const getFlagLabel = (): string => {
    switch (financialAccount?.type) {
      case FinancialAccountTypes.AIRWALLEX_USD:
        return intl.formatMessage(messages.flagAltTextUs)
      case FinancialAccountTypes.AIRWALLEX_CAD:
        return intl.formatMessage(messages.flagAltTextCanada)
      case FinancialAccountTypes.AIRWALLEX_GBP:
        return intl.formatMessage(messages.flagAltTextUk)
      default:
        return ''
    }
  }

  return {
    data: {
      accountBalance: balanceSummary?.totalAmount || 0,
      allocatedFunds: balanceSummary?.allocatedAmount || 0,
      availableFunds: balanceSummary?.availableAmount || 0,
      allocatedToBillPayments:
        balanceSummary?.amountAllocatedToBillPayments || 0,
      allocatedToSpendingCards:
        balanceSummary?.amountAllocatedToSpendingCards || 0,
      currency: balanceSummary?.currency,
      icon: financialAccount?.currencyIcon,
      availableFundsTooltipText: getAvailableFundsTooltipText(),
      flagLabel: getFlagLabel(),
    },
    commands: {
      revealBalanceSummary: () => verifyIdentity(),
    },
    states: {
      showBalanceSummary,
      showInsufficientFundsLabel: balanceSummary?.isInsufficient,
      showLoader:
        isFetchingFinancialAccount ||
        isFetchingBalanceSummary ||
        !financialAccount ||
        isLoadingScaVerification,
      showInactiveAccountLabel:
        !isFetchingFinancialAccount && !financialAccount?.isOperational,
      showSpendingCardsAllocatedFunds: isEntitledToSpendingCards,
      showBillPaymentsAllocatedFunds: isEntitledToBillPayments,
      showShowBalanceButton:
        financialAccount?.isOperational &&
        (financialAccount?.type === FinancialAccountTypes.AIRWALLEX_GBP
          ? !scaToken
          : false),
      isShowBalanceButtonLoading: isLoadingScaVerification,
    },
  }
}
